// Check this job: the two-pass review's own questions, asked of the engine's answer (docs/ai-connections-brief.md,
// Part 1). Pass 1, is everything here: every scope row ended as an item or a dismissal, every item has a number,
// nothing prices at zero under any Build, nothing is numbered only as a child, what is unverified is said. Pass 2,
// is each one right: inputs that disagree between items of one kind in one room, a sub that still prices under a
// Build where the item it belongs to has nothing to do, every engine warning, and the market check per linear foot
// and per square foot against the ranges in settings. A view of resolutions, like the cut list and the labor
// views: it reads the engine's output and prices nothing itself. Deterministic: the same resolutions and rows give
// the same list in the same order.

import type { Library, Resolution, ResolvedItem, Scalar, SubLineIn } from './types.ts';
import { formatLength } from './units.ts';
import { alternateGaps } from './alternates.ts';

export type FindingRule =
  | 'no_scope_rows' | 'scope_unresolved' | 'scope_without_item' | 'no_number' | 'zero_item' | 'zero_line' | 'zero_step' | 'zero_sub'
  | 'number_only_as_child' | 'unverified' | 'alternate_child'
  | 'inputs_disagree' | 'sub_under_build' | 'warning' | 'market_lf' | 'market_sq_ft'
  /** A flag a person raised on the check in their own words (the Quick check pack, 2026-10-24): never from checkJob, read from ug.job_flag by itemFindings. */
  | 'flag';

export interface Finding {
  pass: 1 | 2;
  rule: FindingRule;
  /** What is wrong, in plain words, with the values involved. */
  what: string;
  /** The job item it is on, by number. */
  item?: string;
  /** The items it is spread across, by number. */
  items?: string[];
  /** The Builds it holds under, when not under every one. */
  builds?: string[];
  /** The values involved, by name, where they are worth having apart from the words. */
  values?: Record<string, string>;
}

export interface CheckBuild {
  id: string;
  name: string;
  is_base: boolean;
  resolution: Resolution;
}

/** One Add-on taken under one Build: the Build's id (its base resolution is the CheckBuild of that id) and the job priced with the Add-on on. */
export interface CheckAddOn {
  build_id: string;
  name: string;
  resolution: Resolution;
}

export interface CheckScopeRow {
  id: string;
  request_text: string;
  outcome: string;
  /** The items it became, by id (several when one line is more than one box; 2026-10-18); empty for the whole job, pending or dismissed. */
  job_item_ids: string[];
  /** True when every item it became is on another job of the same opportunity (the option job): not this job's to answer for. */
  item_elsewhere?: boolean;
}

export interface MarketRange {
  build_type: string;
  low: number | null;
  high: number | null;
  no_comp?: boolean;
}

export interface CheckInput {
  lib: Library;
  job: { linear_feet: Scalar; build_type: string | null };
  /** Every Build of the job, each resolved; the base first. */
  builds: CheckBuild[];
  /** Each Add-on priced under each Build (2026-10-17): its alternates' children waiting on a decision, and the warnings on its own items. */
  addOns?: CheckAddOn[];
  scopeRows: CheckScopeRow[];
  /** Every stored item on the job, an add-on's items included (2026-10-22): a scope row pointing at an item in an add-on is answered, not 'no longer on this job'. Missing: the base resolution's items. */
  jobItemIds?: string[];
  subLines: SubLineIn[];
  settings: {
    /** The market_ranges setting: per build type, dollars per linear foot. */
    marketRanges: MarketRange[];
    /** The countertop_market_range setting: which catalog family, dollars per square foot. Blank bounds mean no range set. */
    countertopRange: { family?: string | null; low: Scalar; high: Scalar } | null;
    /** The check_compare_inputs setting: the input keys compared between items of one kind (depth, height, width, legs…). */
    compareInputs: string[];
    /** The check_skip_inputs setting: input keys never compared across top-level items (width, length: boxes differ by design). */
    skipInputs: string[];
  };
}

export interface JobWarning {
  /** Where the engine raised it: the job's standards, one item, the job route (its own steps and lines, typed on Job setup), a kind of item, one route step or one material line shared by items. */
  on: 'the job' | 'item' | 'job route' | 'items of one kind' | 'route step' | 'material line';
  item?: string;
  items?: string[];
  kind?: string;
  step?: string;
  line?: string;
  warning: string;
}

const numberOf = (res: Resolution) => new Map<string, string>(res.items.map((i) => [i.id, i.number || i.name || i.catalog_item_name || i.id]));

/** Every warning the engine raised, each with where it is. One entry per warning counted, so the list is as long as totals.warnings. */
export function listWarnings(res: Resolution): JobWarning[] {
  const num = numberOf(res);
  const out: JobWarning[] = [];
  for (const warning of res.warnings) out.push({ on: 'the job', warning });
  for (const i of res.items) {
    for (const warning of [...i.warnings, ...i.rule_warnings]) out.push({ on: 'item', item: num.get(i.id), warning });
  }
  for (const g of res.groups) {
    if (g.kind === 'job') {
      for (const warning of g.warnings) out.push({ on: 'job route', warning });
      for (const s of g.steps) for (const warning of s.warnings) out.push({ on: 'job route', step: s.operation_name, warning });
      for (const l of g.bom) for (const warning of l.warnings) out.push({ on: 'job route', line: l.description, warning });
      continue;
    }
    const shared = { items: g.item_ids.map((id) => num.get(id) ?? id), kind: g.recipe_name ? `${g.name} · ${g.recipe_name}` : g.name };
    for (const warning of g.warnings) out.push({ on: 'items of one kind', ...shared, warning });
    for (const s of g.steps) for (const warning of s.warnings) out.push({ on: 'route step', step: s.operation_name, ...shared, warning });
    for (const l of g.bom) for (const warning of l.warnings) out.push({ on: 'material line', line: l.description, ...shared, warning });
  }
  return out;
}

const cents = (c: number) => {
  const abs = Math.abs(Math.trunc(c));
  return `${c < 0 ? '-' : ''}$${Math.floor(abs / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}.${(abs % 100).toString().padStart(2, '0')}`;
};
const whole = (c: number) => `$${Math.round(c / 100).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
const blank = (v: Scalar | undefined) => v === null || v === undefined || v === '';
const num = (v: Scalar | undefined): number | null => (blank(v) || !Number.isFinite(Number(v)) ? null : Number(v));

/** A value as the shop reads it: inches as fractions, a material by its description, the rest as typed. */
export function showCheckValue(lib: Library, value: Scalar, unit: string | null, dataType: string): string {
  if (blank(value)) return '(blank)';
  if (dataType === 'material') return lib.materials[String(value)]?.description ?? String(value);
  if (dataType === 'catalog_item') return lib.catalog[String(value)]?.name ?? String(value);
  if (dataType === 'number' && (unit === 'in' || unit === null) && num(value) !== null) return `${formatLength(String(value))}"`;
  if (dataType === 'number' && unit) return `${value} ${unit}`;
  return String(value);
}

const byNumber = (a: string | undefined, b: string | undefined) => String(a ?? '').localeCompare(String(b ?? ''), undefined, { numeric: true });

export function checkJob(input: CheckInput): Finding[] {
  const { lib, builds, settings } = input;
  const base = builds.find((b) => b.is_base) ?? builds[0];
  if (!base) return [];
  const res = base.resolution;
  const nums = numberOf(res);
  const number = (i: ResolvedItem) => nums.get(i.id) ?? i.id;
  const out: Finding[] = [];
  const every = builds.length;

  // A finding that holds under some Builds: collected by key, the Builds it holds under listed when it is not all of them.
  const perBuild = new Map<string, { finding: Finding; builds: string[] }>();
  const under = (build: CheckBuild, key: string, finding: Finding) => {
    const got = perBuild.get(key);
    if (got) got.builds.push(build.name);
    else perBuild.set(key, { finding, builds: [build.name] });
  };
  const flush = () => {
    for (const { finding, builds: names } of perBuild.values()) out.push(names.length === every ? finding : { ...finding, builds: names });
    perBuild.clear();
  };

  // ---------- pass 1 ----------

  // every scope row ends as an item or a dismissal
  const itemIds = new Set([...res.items.map((i) => i.id), ...(input.jobItemIds ?? [])]);
  if (input.scopeRows.length === 0 && res.items.length > 0) {
    out.push({ pass: 1, rule: 'no_scope_rows', what: 'No scope rows on this opportunity, so pass 1 cannot tell what the client asked for that is not here.' });
  }
  for (const row of input.scopeRows) {
    if (row.outcome === 'pending') out.push({ pass: 1, rule: 'scope_unresolved', what: `Scope row has no outcome yet: ${row.request_text}` });
    else if (row.outcome === 'item' && row.job_item_ids.length === 0) out.push({ pass: 1, rule: 'scope_without_item', what: `Scope row says it became an item, but no item is linked: ${row.request_text}` });
    else if (row.outcome === 'item' && row.job_item_ids.some((id) => !itemIds.has(id)) && !row.item_elsewhere) {
      out.push({ pass: 1, rule: 'scope_without_item', what: `Scope row points at an item that is no longer on this job: ${row.request_text}` });
    }
    // outcome job: it applies to the whole job and names no item; resolved
  }

  // every job item has a number
  for (const i of res.items) {
    if (!i.number.trim()) out.push({ pass: 1, rule: 'no_number', item: i.name || i.catalog_item_name || i.id, what: `${i.name || i.catalog_item_name || 'An item'} has no number.` });
  }

  // nothing numbered only as a child of another item
  for (const i of res.items) {
    if (!i.parent_item_id || !i.stored) continue;
    const parent = res.items.find((p) => p.id === i.parent_item_id);
    if (!parent || !i.number.trim() || !parent.number.trim()) continue;
    if (!i.number.startsWith(`${parent.number}.`)) {
      out.push({ pass: 1, rule: 'number_only_as_child', item: i.number, what: `${i.number} is entered only as a child of ${parent.number}; a numbered item of its own is not on the job.` });
    }
  }

  // nothing priced at zero, under the base build or any other
  for (const b of builds) {
    const r = b.resolution;
    const n = numberOf(r);
    for (const i of r.items) {
      if (i.price_cents !== 0 || i.mark_threshold === 'no_mark') continue;
      const empty = i.is_custom && Number(i.labor_minutes) === 0 && i.material_cents === 0;
      const name = i.name || i.catalog_item_name || '';
      under(b, `zero_item:${i.id}`, {
        pass: 1, rule: 'zero_item', item: n.get(i.id), what: `${n.get(i.id)}${name ? ` · ${name}` : ''}: $0.00${empty ? ', nothing entered on it' : ''}.`,
      });
    }
    for (const g of r.groups) {
      const items = g.item_ids.map((id) => n.get(id) ?? id).sort(byNumber);
      for (const s of g.steps) {
        if (s.cents !== 0) continue;
        if (g.kind === 'job') under(b, `zero_step:job:${s.operation_id}`, { pass: 1, rule: 'zero_step', what: `Job route · ${s.operation_name}: $0.00${s.warnings.length ? ', nothing typed yet' : ''}.` });
        else if (Number(s.qty_raw) > 0) under(b, `zero_step:${g.key}:${s.key}`, { pass: 1, rule: 'zero_step', items, what: `${g.name} · ${s.operation_name}: ${s.qty} ${s.unit ?? 'units'} at $0.00 (no time standard or rate).` });
      }
      for (const l of g.bom) {
        if (!l.typed_price) continue;
        for (const id of g.item_ids) {
          const share = l.shares[id];
          if (share && share.cents !== 0) continue;
          const named = l.warnings.some((w) => w.includes(n.get(id) ?? '\u0000'));
          under(b, `zero_line:${id}:${l.line_id ?? l.key}`, { pass: 1, rule: 'zero_line', item: n.get(id), what: `${n.get(id)} · ${l.description}: ${named ? 'nothing typed yet, $0.00' : '$0.00'}.` });
        }
      }
    }
  }
  flush();

  // an alternate that is another catalog item: a child the item it replaces makes and it does not (an applied end panel)
  // waits on a decision, kept under the alternate or left out (Spencer, 2026-10-17), under the Builds where it happens
  for (const a of input.addOns ?? []) {
    const b = builds.find((x) => x.id === a.build_id);
    if (!b) continue;
    for (const g of alternateGaps(b.resolution, a.resolution)) {
      under(b, `alternate_child:${g.alternate_id}:${g.child.id}`, {
        pass: 1, rule: 'alternate_child', item: g.alternate_number, items: [g.alternate_number, g.child.number],
        what: `${g.alternate_number} stands in for ${g.replaced_number} in "${a.name}": ${g.replaced_number} has ${g.child.number} ${g.child.name}, which ${g.alternate_number} does not make. Keep it under ${g.alternate_number} or leave it out (Builds tab).`,
      });
    }
  }
  flush();

  // a sub line at nothing
  for (const s of input.subLines) {
    const used = s.use_quote ? s.quote_amount : s.internal_estimate;
    if (blank(used) || Number(used) === 0) out.push({ pass: 1, rule: 'zero_sub', what: `Sub line "${s.scope_text}": ${blank(used) ? 'no number typed' : '$0.00'}.` });
  }

  // library content used that is unverified: one line per kind of item and recipe, with the items that use it
  for (const b of builds) {
    const r = b.resolution;
    const n = numberOf(r);
    const kinds = new Map<string, { what: string; items: string[] }>();
    for (const i of r.items) {
      if (!i.unverified.any) continue;
      const parts: string[] = [];
      if (i.unverified.recipe) parts.push(`the ${i.recipe_name ?? ''} recipe`.replace('the  recipe', 'the recipe'));
      if (i.unverified.operations) parts.push(`${i.unverified.operations} operation${i.unverified.operations === 1 ? '' : 's'}`);
      if (i.unverified.materials) parts.push(`${i.unverified.materials} material${i.unverified.materials === 1 ? '' : 's'}`);
      const what = i.is_custom ? 'a custom item, never verified' : parts.length ? `${parts.join(', ')} unverified` : 'unverified';
      const kind = i.is_custom ? i.name || i.catalog_item_name || 'custom item' : `${i.catalog_item_name ?? i.name}${i.recipe_name ? ` · ${i.recipe_name}` : ''}`;
      const key = `${i.is_custom ? i.id : i.catalog_item_id}|${i.recipe_id ?? ''}|${what}`;
      const got = kinds.get(key) ?? { what: `${kind}: ${what}`, items: [] };
      got.items.push(n.get(i.id) ?? i.id);
      kinds.set(key, got);
    }
    for (const [key, k] of kinds) {
      const items = k.items.sort(byNumber);
      under(b, `unverified:${key}`, { pass: 1, rule: 'unverified', ...(items.length === 1 ? { item: items[0] } : { items }), what: `${items.join(', ')} · ${k.what}.` });
    }
    const jobGroup = r.groups.find((g) => g.kind === 'job');
    for (const s of jobGroup?.steps ?? []) {
      if (!s.verified) under(b, `unverified:job:${s.operation_id}`, { pass: 1, rule: 'unverified', what: `Job route · ${s.operation_name}: operation unverified.` });
    }
  }
  flush();

  // ---------- pass 2 ----------

  // inputs that disagree between items of one kind in one room. One kind: top-level items of one catalog family standing
  // the same height (the bases along a wall, not the bases against the uppers); children of one catalog item (every slab
  // door). Only the inputs the setting names are compared (depth, height, width, legs…); a box's own width never across boxes.
  const skip = new Set(settings.skipInputs);
  const compare = new Set(settings.compareInputs);
  const groups = new Map<string, ResolvedItem[]>();
  for (const i of res.items) {
    if (i.is_custom || !i.catalog_item_id) continue;
    const height = i.inputs.find((x) => x.key === 'height' && !x.hidden)?.value;
    const kind = i.parent_item_id ? `item:${i.catalog_item_id}` : `family:${lib.catalog[i.catalog_item_id]?.family ?? i.catalog_item_id}|height:${blank(height) ? '' : String(height)}`;
    const key = `${i.room_id ?? ''}|${kind}`;
    groups.set(key, [...(groups.get(key) ?? []), i]);
  }
  for (const [key, items] of groups) {
    if (items.length < 2) continue;
    const topLevel = key.includes('|family:');
    const keys = new Map<string, { label: string; unit: string | null; data_type: string; values: Map<string, string[]> }>();
    for (const i of items) {
      for (const inp of i.inputs) {
        if (inp.hidden || blank(inp.value) || !compare.has(inp.key)) continue;
        if (topLevel && skip.has(inp.key)) continue;
        const k = keys.get(inp.key) ?? { label: inp.label, unit: inp.unit, data_type: String(inp.data_type), values: new Map() };
        const shown = showCheckValue(lib, inp.value, inp.unit, String(inp.data_type));
        k.values.set(shown, [...(k.values.get(shown) ?? []), number(i)]);
        keys.set(inp.key, k);
      }
    }
    const kindName = topLevel ? lib.catalog[items[0]!.catalog_item_id!]?.family ?? 'items' : items[0]!.catalog_item_name ?? 'items';
    for (const [, k] of keys) {
      if (k.values.size < 2) continue;
      const covered = [...k.values.values()].reduce((n, v) => n + v.length, 0);
      if (covered < 2) continue;
      const sets = [...k.values.entries()].sort((a, b) => b[1].length - a[1].length || byNumber(a[1][0], b[1][0]));
      const values: Record<string, string> = {};
      for (const [v, nums] of sets) values[nums.sort(byNumber).join(', ')] = v;
      out.push({
        pass: 2, rule: 'inputs_disagree', items: sets.flatMap(([, nums]) => nums).sort(byNumber), values,
        what: `${k.label} differs across the ${kindName}: ${sets.map(([v, nums]) => `${nums.join(', ')} at ${v}`).join('; ')}.`,
      });
    }
  }

  // a sub estimate typed on an item that sends its vendor nothing: the item's rule leaves it out of the price (Spencer,
  // 2026-10-17), and the check says so, under the Builds where it happens
  for (const b of builds) {
    const r = b.resolution;
    const n = numberOf(r);
    for (const off of r.sub_lines_off ?? []) {
      if (off.cents === 0) continue;
      under(b, `sub_under_build:${off.sub_line_id}`, {
        pass: 2, rule: 'sub_under_build', item: n.get(off.item_id),
        // left out by hand on the Subs tab (2026-10-24): said as such, so a cell left wrong is seen
        what: off.by_hand
          ? `Sub line "${off.scope_text}" (${cents(off.cents)})${off.item_id ? ` on ${n.get(off.item_id) ?? 'its item'}` : ''} is left out of this price by hand (the Subs tab).`
          : `Sub line "${off.scope_text}" (${cents(off.cents)}) on ${n.get(off.item_id)} has nothing going to the vendor (${off.rule}), so it is left out of the price.`,
      });
    }
  }
  // it holds under no Build at all only when the list is empty; a sub that is dead under every Build is the job's
  flush();

  // every engine warning, where it is; a warning only another Build raises says which
  const seen = new Set<string>();
  for (const b of [base, ...builds.filter((x) => x !== base)]) {
    for (const w of listWarnings(b.resolution)) {
      const key = `${w.on}|${w.item ?? ''}|${(w.items ?? []).join(',')}|${w.step ?? ''}|${w.line ?? ''}|${w.warning}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const where = w.item ? w.item : w.items?.length ? w.items.join(', ') : w.on;
      const at = w.step ? ` · ${w.step}` : w.line ? ` · ${w.line}` : '';
      out.push({ pass: 2, rule: 'warning', item: w.item, items: w.items, ...(b === base ? {} : { builds: [b.name] }), what: `${where}${at}: ${w.warning}` });
    }
  }
  // an add-on's own items are priced only with it on: their warnings (an alternate's vendor price not typed yet) are said
  // with the Build and the Add-on (2026-10-17)
  for (const a of input.addOns ?? []) {
    const b = builds.find((x) => x.id === a.build_id);
    if (!b) continue;
    const held = new Set(a.resolution.items.filter((i) => i.add_on_id).map((i) => i.number || i.name || i.id));
    for (const w of listWarnings(a.resolution)) {
      const on = [w.item, ...(w.items ?? [])].filter(Boolean) as string[];
      if (!on.some((x) => held.has(x))) continue;
      const key = `${w.on}|${w.item ?? ''}|${(w.items ?? []).join(',')}|${w.step ?? ''}|${w.line ?? ''}|${w.warning}`;
      if (seen.has(key)) continue;
      seen.add(key);
      const where = w.item ? w.item : w.items?.length ? w.items.join(', ') : w.on;
      const at = w.step ? ` · ${w.step}` : w.line ? ` · ${w.line}` : '';
      out.push({ pass: 2, rule: 'warning', item: w.item, items: w.items, builds: [`${b.name} + ${a.name}`], what: `${where}${at}: ${w.warning}` });
    }
  }

  // the market check per linear foot for each Build, and per square foot for each countertop
  const market = marketCheck(input);
  for (const m of market.per_linear_foot) {
    const values: Record<string, string> = { price: cents(m.price_cents) };
    let what: string;
    if (m.per_foot_cents === null) what = `${m.build}: ${cents(m.price_cents)}; ${m.verdict}.`;
    else {
      values.per_linear_foot = whole(m.per_foot_cents);
      values.linear_feet = String(m.linear_feet);
      what = `${m.build}: ${cents(m.price_cents)} over ${m.linear_feet} LF is ${whole(m.per_foot_cents)}/LF, ${m.verdict}.`;
    }
    out.push({ pass: 2, rule: 'market_lf', what, values });
  }
  for (const t of market.countertops) {
    if (t.square_feet === null) {
      out.push({ pass: 2, rule: 'market_sq_ft', item: t.item, what: `${t.item}: no length and depth, so the per-square-foot check cannot run.` });
      continue;
    }
    const values: Record<string, string> = { square_feet: t.square_feet };
    const parts: string[] = [];
    for (const b of t.by_build) {
      values[b.build] = `${cents(b.price_cents)}, ${cents(b.per_sq_ft_cents)}/sq ft`;
      parts.push(`${cents(b.per_sq_ft_cents)}/sq ft under ${b.build}${b.verdict ? `, ${b.verdict}` : ''}`);
    }
    out.push({ pass: 2, rule: 'market_sq_ft', item: t.item, values, what: `${t.item}: ${t.square_feet} sq ft; ${parts.join('; ')}${market.countertop_range ? '' : ' (no countertop range set)'}.` });
  }

  // pass, then rule, then item number; what is on no item (the job route, a scope row) after the items
  const place = (f: Finding) => f.item ?? f.items?.[0] ?? '￿';
  return out.sort((a, b) => a.pass - b.pass || RULE_ORDER.indexOf(a.rule) - RULE_ORDER.indexOf(b.rule) || byNumber(place(a), place(b)));
}

export interface MarketCheck {
  /** The range for the job's build type, or null when none is set. */
  range: { build_type: string; low: number; high: number } | null;
  countertop_range: { low: number; high: number } | null;
  per_linear_foot: { build: string; price_cents: number; linear_feet: number | null; per_foot_cents: number | null; verdict: string }[];
  countertops: { item: string; square_feet: string | null; by_build: { build: string; price_cents: number; per_sq_ft_cents: number; verdict: string }[] }[];
}

/** The market check as figures: the Price tab's per-linear-foot line for each Build, and each countertop per square foot. */
export function marketCheck(input: CheckInput): MarketCheck {
  const { lib, builds, settings } = input;
  const base = builds.find((b) => b.is_base) ?? builds[0];
  const lf = num(input.job.linear_feet);
  const r = settings.marketRanges.find((x) => x.build_type === input.job.build_type);
  const range = r && !r.no_comp && r.low !== null && r.high !== null && input.job.build_type ? { build_type: input.job.build_type, low: r.low, high: r.high } : null;
  const band = range ? `$${range.low.toLocaleString()}–${range.high.toLocaleString()}` : null;
  const per_linear_foot = builds.map((b) => {
    const price_cents = b.resolution.totals.price_cents;
    if (!input.job.build_type) return { build: b.name, price_cents, linear_feet: lf, per_foot_cents: null, verdict: 'no build type set on Job setup, so there is no market range to check against' };
    if (r?.no_comp) return { build: b.name, price_cents, linear_feet: lf, per_foot_cents: null, verdict: `${input.job.build_type} has no per-foot comp` };
    if (!lf || lf <= 0) return { build: b.name, price_cents, linear_feet: lf, per_foot_cents: null, verdict: 'no linear feet entered on Job setup, so the per-foot check cannot run' };
    const per_foot_cents = Math.round(price_cents / lf);
    const verdict = !band ? `no range set for ${input.job.build_type}` : per_foot_cents > range!.high * 100 ? `above ${band}` : per_foot_cents < range!.low * 100 ? `below ${band}` : `within ${band}`;
    return { build: b.name, price_cents, linear_feet: lf, per_foot_cents, verdict };
  });
  const ct = settings.countertopRange;
  const low = num(ct?.low);
  const high = num(ct?.high);
  const countertop_range = low !== null && high !== null ? { low, high } : null;
  const ctBand = countertop_range ? `$${low!.toLocaleString()}–${high!.toLocaleString()}` : null;
  const countertops: MarketCheck['countertops'] = [];
  const family = ct?.family ?? null;
  if (family && base) {
    const nums = numberOf(base.resolution);
    for (const t of base.resolution.items) {
      if (t.parent_item_id || !t.catalog_item_id || lib.catalog[t.catalog_item_id]?.family !== family) continue;
      const item = nums.get(t.id) ?? t.id;
      // the inputs that play length and depth on this item: declared on the catalog item (dimension_roles, 2026-10-23), else the inputs
      // keyed so, else (2026-10-29) the one number input whose key or label says length (or run) or depth, whatever it is named
      // ("Countertop run length" on the window sill)
      const roles = lib.catalog[t.catalog_item_id]?.dimension_roles ?? {};
      const dim = (role: 'length' | 'depth'): number | null => {
        if (roles[role]) return num(t.inputs.find((x) => x.key === roles[role])?.value);
        const keyed = t.inputs.find((x) => x.key === role);
        if (keyed) return num(keyed.value);
        const says = role === 'length' ? /length|\brun\b/i : /depth/i;
        const named = t.inputs.filter((x) => x.data_type === 'number' && says.test(`${x.key} ${x.label}`));
        return named.length === 1 ? num(named[0]!.value) : null;
      };
      const length = dim('length');
      const depth = dim('depth');
      if (!length || !depth) {
        countertops.push({ item, square_feet: null, by_build: [] });
        continue;
      }
      const sqFt = (length * depth) / 144;
      const by_build = builds.flatMap((b) => {
        const it = b.resolution.items.find((i) => i.id === t.id);
        if (!it) return [];
        const per_sq_ft_cents = Math.round(it.rollup_price_cents / sqFt);
        const verdict = !ctBand ? '' : per_sq_ft_cents > high! * 100 ? `above ${ctBand}` : per_sq_ft_cents < low! * 100 ? `below ${ctBand}` : `within ${ctBand}`;
        return [{ build: b.name, price_cents: it.rollup_price_cents, per_sq_ft_cents, verdict }];
      });
      countertops.push({ item, square_feet: sqFt.toFixed(2), by_build });
    }
  }
  return { range, countertop_range, per_linear_foot, countertops };
}

const RULE_ORDER: FindingRule[] = [
  'no_scope_rows', 'scope_unresolved', 'scope_without_item', 'no_number', 'number_only_as_child', 'zero_item', 'zero_line', 'zero_step', 'zero_sub', 'unverified',
  'inputs_disagree', 'sub_under_build', 'warning', 'market_lf', 'market_sq_ft',
];
